/*
 * PDP (redesign) entry script.
 *
 * Lean, no-build. Progressive enhancement over server-rendered markup: size
 * selection, drawers (size, colour, details), details tabs, gallery indicators,
 * accordion animation, and AJAX add-to-cart via AT.cart.
 *
 * Hook classes (never styled): .js-pdp*
 */
(function initPdp() {
  var onReady = function onDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  };

  /* ---------- Shared add-to-cart (desktop ATC + mobile drawer both use this) ---------- */
  var cartSectionIds = function cartSectionIds() {
    var cartDrawer = document.querySelector('cart-drawer');
    return cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map(function (s) {
          return s.id;
        })
      : [];
  };

  var addToCart = function addToCart(variantId) {
    if (!variantId || !window.AT || !window.AT.cart) return Promise.reject(new Error('cart unavailable'));
    var ids = cartSectionIds();
    return window.AT.cart
      .add({ id: variantId, quantity: 1, sections: ids.length ? ids : undefined })
      .then(function (json) {
        var drawer = document.querySelector('cart-drawer');
        // renderContents updates the drawer + header count and opens the drawer.
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(json);
        } else {
          window.location = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
        }
        document.dispatchEvent(new CustomEvent('AT:cart:updated', { detail: json }));
        return json;
      });
  };

  /* ---------- Form messages (inline, announced via role=alert) ---------- */
  var setError = function setFormError(form, message) {
    var el = form.querySelector('.js-pdpError');
    if (el) el.textContent = message || '';
  };

  // The chosen size radio, or the hidden id of a single-variant product.
  var chosenVariantField = function chosenVariantField(form) {
    return form.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
  };

  var promptForSize = function promptForSize(form) {
    var errorEl = form.querySelector('.js-pdpError');
    setError(form, errorEl && errorEl.dataset.selectSize);
    var firstSize = form.querySelector('.js-pdpSize');
    if (firstSize) firstSize.focus();
  };

  /* Put the chosen size in the URL, so the page can be copied, shared or reloaded on
     it — Liquid reads ?variant= back as product.selected_variant and renders that size
     checked, so the round trip needs nothing else.

     replaceState, not pushState: picking a size is not navigation, and pushing would
     make the Back button step through every size the shopper tried before it left the
     page. Built from the current URL rather than concatenated, so utm_* and anything
     else already in the query string survives. */
  var updateUrlVariant = function updateUrlVariant(variantId) {
    if (!variantId || !window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (err) {
      // Nothing here is load-bearing: the selection works, the URL just stays put.
    }
  };

  /* ---------- Size selection: show the chosen size's server-rendered price + scarcity ---------- */
  var initSizeSelection = function initPdpSizeSelection() {
    var form = document.querySelector('.js-pdpForm');
    if (!form) return;

    var price = document.querySelector('.js-pdpPrice');
    var comparePrice = document.querySelector('.js-pdpComparePrice');
    var scarcity = form.querySelector('.js-pdpScarcity');
    var atc = form.querySelector('.js-pdpAtc');
    var notifyCta = form.querySelector('.js-pdpNotifyCta');

    // Sold-out sizes render disabled so the no-JS form can't post them. The waitlist
    // needs JS, so here they become selectable — choosing one switches the CTA to "Notify me".
    form.querySelectorAll('.js-pdpSize:disabled').forEach(function (input) {
      input.disabled = false;
    });

    var setOptionalText = function setOptionalText(el, text) {
      if (!el) return;
      el.textContent = text || '';
      el.hidden = !text;
    };

    form.addEventListener('change', function onSizeChange(ev) {
      var input = ev.target;
      if (!input.classList.contains('js-pdpSize')) return;

      var available = input.dataset.available === 'true';
      if (atc) atc.hidden = !available;
      if (notifyCta) notifyCta.hidden = available;

      if (price) price.textContent = input.dataset.price;
      setOptionalText(comparePrice, input.dataset.comparePrice);
      setOptionalText(scarcity, input.dataset.scarcity);
      setError(form, '');
      updateUrlVariant(input.value);
    });
  };

  /* ---------- Notify me (waitlist): opens the back-in-stock drawer for the chosen
     size. No backend yet — submitting fires "pdp:notify:submit" with
     { variantId, email } for the waitlist provider (Klaviyo) to pick up. ---------- */
  var initNotify = function initPdpNotify(sizeDrawer) {
    var form = document.querySelector('.js-pdpForm');
    var drawer = document.querySelector('.js-pdpNotifyDrawer');
    if (!form || !drawer || !window.AT || !window.AT.drawer) return;

    var notifyDrawer = window.AT.drawer.create(drawer, {
      backdrop: document.querySelector('.js-pdpNotifyDrawerBackdrop'),
      label: (drawer.querySelector('.pdp-info__notify-submit') || {}).textContent || 'Notify me',
    });

    var variantInput = drawer.querySelector('.js-pdpNotifyVariant');
    var message = drawer.querySelector('.js-pdpNotifyMessage');

    // Delegated on document: mobile drawer rows live in <body> (the drawer is portaled).
    document.addEventListener('click', function onNotifyClick(ev) {
      var trigger = ev.target.closest('.js-pdpNotify');
      if (!trigger) return;

      // Drawer rows carry their own variant; the CTA uses the chosen size.
      var field = chosenVariantField(form);
      var variantId = trigger.dataset.variantId || (field && field.value);
      if (!variantId) {
        promptForSize(form);
        return;
      }

      if (variantInput) variantInput.value = variantId;
      if (message) message.textContent = '';
      if (sizeDrawer && sizeDrawer.isOpen()) sizeDrawer.close(); // mobile: swap sheets
      notifyDrawer.open();

      document.dispatchEvent(new CustomEvent('pdp:notify', { detail: { variantId: variantId } }));
    });

    var notifyForm = drawer.querySelector('.js-pdpNotifyForm');
    if (!notifyForm) return;

    if (!(notifyForm.dataset.klaviyoToken || '').trim()) {
      console.warn(
        '[PDP] Klaviyo public API key is not set for this theme ' +
          '(Theme settings → Klaviyo). Waitlist sign-ups will fail.'
      );
    }

    var submitting = false;

    notifyForm.addEventListener('submit', function onNotifySubmit(ev) {
      ev.preventDefault();
      if (submitting) return;

      var email = notifyForm.elements.email;
      var variantId = variantInput && variantInput.value;
      if (!email || !email.value || !variantId) return;

      var setMessage = function setNotifyMessage(text, isError) {
        if (!message) return;
        message.textContent = text || '';
        message.classList.toggle('pdp-info__notify-message--error', !!isError);
      };

      if (!window.AT || !window.AT.klaviyo) {
        setMessage(message && message.dataset.error, true);
        return;
      }

      submitting = true;
      notifyForm.setAttribute('aria-busy', 'true');
      setMessage('');

      window.AT.klaviyo
        .submitWaitlist({
          token: notifyForm.dataset.klaviyoToken,
          email: email.value,
          variantId: variantId,
        })
        .then(function () {
          setMessage(message && message.dataset.success);
          notifyForm.reset();
          document.dispatchEvent(
            new CustomEvent('pdp:notify:subscribed', { detail: { variantId: variantId } })
          );
        })
        .catch(function (err) {
          console.error('[PDP] waitlist sign-up failed', err);
          setMessage((message && message.dataset.error) || 'Something went wrong', true);
        })
        .finally(function () {
          submitting = false;
          notifyForm.removeAttribute('aria-busy');
        });
    });
  };

  /* ---------- Read more → details drawer ---------- */
  var initReadMore = function initPdpDetailsDrawer() {
    var drawer = document.querySelector('.js-pdpDetailsDrawer');
    if (!drawer || !window.AT || !window.AT.drawer) return;

    // "Read More" (only rendered when the copy is truncated) plus the PDP's detail
    // rows, which carry the section they should open on.
    var openers = Array.prototype.slice.call(
      document.querySelectorAll('.js-pdpReadMore, .js-pdpDetailTrigger')
    );
    if (!openers.length) return;

    window.AT.drawer.create(drawer, {
      openers: openers,
      backdrop: document.querySelector('.js-pdpDetailsDrawerBackdrop'),
      label: 'Details',
    });

    // Reveal the requested section: select its tab (mobile) or open its accordion
    // (desktop). Sections are section blocks, so the key is a block id. Fall back to
    // the first section when a key is missing or no longer matches a block.
    var showSection = function showDetailsSection(key) {
      var tabSelector = key ? '[role="tab"][data-detail="' + key + '"]' : null;
      var tab =
        (tabSelector && drawer.querySelector(tabSelector)) || drawer.querySelector('[role="tab"]');
      if (tab && tab.getAttribute('aria-selected') !== 'true') tab.click();

      var panelSelector = key ? 'details[data-detail="' + key + '"]' : null;
      var panel =
        (panelSelector && drawer.querySelector(panelSelector)) || drawer.querySelector('details');
      // One section at a time. The first section is rendered open, so opening the
      // drawer on any other one has to close it too, or two sections show at once.
      drawer.querySelectorAll('.pdp-info__accordion').forEach(function (details) {
        var body = details.querySelector('.pdp-info__accordion-body');
        if (body) body.style.height = ''; // clear a height left by an interrupted animation
        details.open = details === panel;
      });
    };

    openers.forEach(function (opener) {
      opener.addEventListener('click', function onOpenerClick() {
        showSection(opener.dataset.detail);
      });
    });
  };

  /* ---------- Details tabs (mobile details drawer; markup is server-rendered) ---------- */
  var initDetailsTabs = function initPdpDetailsTabs() {
    document.querySelectorAll('.js-pdpTabs').forEach(function (wrap) {
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));

      var select = function selectTab(tab, moveFocus) {
        tabs.forEach(function (t) {
          var active = t === tab;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.tabIndex = active ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        });
        if (moveFocus) tab.focus();
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function onTabClick() {
          select(tab, false);
        });

        // WAI-ARIA tabs pattern: arrows move between tabs, Home/End jump to the ends.
        tab.addEventListener('keydown', function onTabKeydown(ev) {
          var next = {
            ArrowRight: tabs[(i + 1) % tabs.length],
            ArrowLeft: tabs[(i - 1 + tabs.length) % tabs.length],
            Home: tabs[0],
            End: tabs[tabs.length - 1],
          }[ev.key];
          if (!next) return;
          ev.preventDefault();
          select(next, true);
        });
      });
    });
  };

  /* ---------- Colour hover preview (desktop): show that colour's first gallery
     image over the current product's first one, on hover and on focus ---------- */
  var initColourPreview = function initPdpColourPreview() {
    var layers = Array.prototype.slice.call(document.querySelectorAll('.js-pdpGalleryPreview'));
    var thumbs = document.querySelectorAll('.js-pdpColourPreview');
    if (layers.length < 2 || !thumbs.length) return;

    // Desktop only: below this the gallery is a full-width carousel and the preview
    // is hidden, so there is no point fetching the image.
    var desktop = window.matchMedia('(min-width: 990px)');
    var activeSrc = '';
    // The layer that showed last. Kept after hiding: moving between two thumbs fires
    // mouseleave before mouseenter, and if we forgot it we would reuse the layer that
    // is still fading out — swapping its src mid-fade, which reads as no transition.
    var lastLayer = null;

    var reveal = function revealLayer(layer, src) {
      if (activeSrc !== src) return; // pointer/focus moved on while it loaded
      layer.style.zIndex = '2';
      layer.classList.add('is-visible');
      layers.forEach(function (other) {
        if (other === layer) return;
        other.style.zIndex = '1';
        other.classList.remove('is-visible'); // fades out as the new one fades in
      });
      lastLayer = layer;
    };

    var show = function showPreview(src, srcset) {
      if (!src || !desktop.matches) return;
      activeSrc = src;

      // Same colour as last time: just fade that layer back in, no reload.
      if (lastLayer && lastLayer.getAttribute('src') === src) {
        lastLayer.style.zIndex = '2';
        lastLayer.classList.add('is-visible');
        return;
      }

      // Always load into the *other* layer, so the one on screen (or still fading
      // out) keeps its pixels while the new colour fades in over it.
      var next = layers[0] === lastLayer ? layers[1] : layers[0];
      if (next.getAttribute('src') === src && next.complete) {
        reveal(next, src);
        return;
      }
      next.onload = function onPreviewLoad() {
        reveal(next, src);
      };
      // srcset before src, so the browser picks the right width for the 50vw frame
      // (sizes is on the markup); src is the fallback for the no-srcset case.
      if (srcset) next.srcset = srcset;
      next.src = src; // first hover/focus triggers the only request
    };

    var hide = function hidePreview() {
      activeSrc = '';
      layers.forEach(function (layer) {
        layer.classList.remove('is-visible');
      });
    };

    thumbs.forEach(function (thumb) {
      var src = thumb.dataset.previewSrc;
      var srcset = thumb.dataset.previewSrcset;
      thumb.addEventListener('mouseenter', function () {
        show(src, srcset);
      });
      thumb.addEventListener('focus', function () {
        show(src, srcset);
      });
      thumb.addEventListener('mouseleave', hide);
      thumb.addEventListener('blur', hide);
    });
  };

  /* ---------- Size guide modal: opened from inside the details drawer ---------- */
  var initSizeGuide = function initPdpSizeGuide() {
    var modal = document.getElementById('ModalDrawer-SizeGuide');
    if (!modal) return;

    var drawerOpen = function anyPdpDrawerOpen() {
      // at-drawer marks the open drawer itself; its backdrop has no aria-modal.
      return !!document.querySelector('.is-open[aria-modal="true"]');
    };

    // The theme's modal clears body.overflow-hidden on close, but the size guide is
    // opened from the details drawer, which set that same class. Put it back so the
    // page behind the still-open drawer stays locked.
    document.body.addEventListener('modalClosed', function () {
      if (drawerOpen()) document.body.classList.add('overflow-hidden');
    });

    // The modal closes itself on Escape (keyup, on the element), but at-drawer listens
    // for keydown on the document, so Escape would also close the drawer underneath.
    // Capture it first. The modal only sees the key while focus is inside it, which
    // trapFocus arranges ~100ms after opening — close it here if it is not.
    document.addEventListener(
      'keydown',
      function (ev) {
        if (ev.key !== 'Escape' || !modal.hasAttribute('open')) return;
        ev.stopPropagation();
        if (!modal.contains(document.activeElement) && typeof modal.hide === 'function') {
          modal.hide();
        }
      },
      true
    );
  };

  /* ---------- Gallery videos: silent, looping, no controls ---------- */
  var initGalleryVideos = function initPdpGalleryVideos() {
    document.querySelectorAll('.pdp-gallery__media--video video').forEach(function (video) {
      // Browsers only autoplay muted, inline video; Safari needs both set on the
      // element (not just the attributes) before play() is allowed.
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.controls = false;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');

      var play = video.play();
      if (play && typeof play.catch === 'function') {
        play.catch(function () {
          /* autoplay refused (e.g. battery saver) — the poster frame stays */
        });
      }
    });
  };

  /* ---------- Mobile gallery scroll indicator (line, not dots) ---------- */
  var initGalleryScrollbar = function initPdpGalleryScrollbar() {
    var track = document.querySelector('.js-pdpGalleryTrack');
    if (!track || !window.AT || !window.AT.customScrollbar) return;
    window.AT.customScrollbar.attach(track, '.js-pdpGalleryBar');
  };

  /* ---------- Desktop gallery dots (scroll-spy on the stacked images) ---------- */
  var initGalleryDots = function initPdpGalleryDots() {
    var gallery = document.querySelector('.js-pdpGallery');
    var dotsWrap = gallery && gallery.querySelector('.js-pdpGalleryDots');
    if (!gallery || !dotsWrap) return;

    var dots = dotsWrap.querySelectorAll('.js-pdpGalleryDot');
    var items = gallery.querySelectorAll('.pdp-gallery__item');
    if (!dots.length || !items.length) return;

    var setActive = function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('pdp-gallery__dot--active', i === index);
      });
    };

    if ('IntersectionObserver' in window) {
      // Active = the image crossing the vertical centre of the viewport.
      var observer = new IntersectionObserver(
        function onIntersect(entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              setActive(parseInt(entry.target.dataset.pdpGalleryIndex, 10) || 0);
            }
          });
        },
        { root: null, rootMargin: '-50% 0px -50% 0px', threshold: 0 }
      );
      items.forEach(function (item) {
        observer.observe(item);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function onDotClick() {
        var idx = parseInt(dot.dataset.index, 10) || 0;
        if (items[idx]) items[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  };

  /* ---------- Add to cart: AJAX + cart drawer on top of the native form post ---------- */
  var initAddToCart = function initPdpAddToCart(sizeDrawer) {
    var form = document.querySelector('.js-pdpForm');
    if (!form || !window.AT || !window.AT.cart) return; // not enhanced: the form posts to /cart/add

    form.noValidate = true; // the size choice is validated below, with an inline message
    var busy = false;

    form.addEventListener('submit', function onSubmit(ev) {
      if (form.dataset.nativeSubmit) return; // fallback path (see below): let the browser post
      ev.preventDefault();
      if (busy) return;

      // Mobile drawer sizes submit with their own variant id; otherwise use the chosen size.
      var submitter = ev.submitter;
      var idField = submitter && submitter.name === 'id' ? submitter : chosenVariantField(form);

      if (!idField) {
        promptForSize(form);
        return;
      }

      // A drawer tap adds a size without ever selecting it, which would leave the page
      // behind the cart drawer — price, scarcity, ?variant= — on whichever size it
      // opened on. Select it here and the rest follows through the change handler.
      if (submitter && submitter.name === 'id') {
        var chosen = form.querySelector('.js-pdpSize[value="' + idField.value + '"]');
        if (chosen && !chosen.checked) {
          chosen.checked = true;
          chosen.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (sizeDrawer && sizeDrawer.isOpen()) sizeDrawer.close(); // the cart drawer opens next
      setError(form, '');

      var button = submitter || form.querySelector('.js-pdpAtc');
      busy = true;
      if (button) button.setAttribute('aria-busy', 'true');

      addToCart(idField.value)
        .catch(function (err) {
          // Shopify's 422 carries a customer-facing description (e.g. sold out, cart limit).
          if (err && err.description) {
            setError(form, err.description);
            return;
          }
          // Anything else (network, unexpected response): fall back to a regular form post.
          form.dataset.nativeSubmit = 'true';
          form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
        })
        .finally(function () {
          busy = false;
          if (button) button.removeAttribute('aria-busy');
        });
    });
  };

  /* ---------- Accordions (animated height; native <details> when no JS) ---------- */
  var initAccordions = function initPdpAccordions() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('.pdp-info__accordion').forEach(function (details) {
      var summary = details.querySelector('.pdp-info__accordion-summary');
      var body = details.querySelector('.pdp-info__accordion-body');
      if (!summary || !body) return;

      var animating = false;

      var whenDone = function whenTransitionDone(closing) {
        return function handler(ev) {
          if (ev.propertyName !== 'height') return;
          body.style.height = ''; // back to auto so content can reflow
          if (closing) details.open = false;
          body.removeEventListener('transitionend', handler);
          animating = false;
        };
      };

      summary.addEventListener('click', function onSummaryClick(ev) {
        ev.preventDefault(); // manage the open state ourselves so we can animate
        if (reduce) {
          details.open = !details.open;
          return;
        }
        if (animating) return;
        animating = true;

        if (!details.open) {
          details.open = true;
          var target = body.scrollHeight;
          body.style.height = '0px';
          void body.offsetHeight; // force reflow so the transition starts from 0
          body.style.height = target + 'px';
          body.addEventListener('transitionend', whenDone(false));
        } else {
          body.style.height = body.scrollHeight + 'px';
          void body.offsetHeight;
          body.style.height = '0px';
          body.addEventListener('transitionend', whenDone(true));
        }
      });
    });
  };

  /* ---------- Mobile size drawer (bottom sheet; its size buttons submit the product form) ---------- */
  var initSizeDrawer = function initPdpSizeDrawer() {
    var drawer = document.querySelector('.js-pdpSizeDrawer');
    var opener = document.querySelector('.js-pdpSizeDrawerOpen');
    if (!drawer || !opener || !window.AT || !window.AT.drawer) return null;

    return window.AT.drawer.create(drawer, {
      openers: [opener],
      backdrop: document.querySelector('.js-pdpSizeDrawerBackdrop'),
      label: opener.textContent.trim(),
    });
  };

  /* ---------- Colour selection drawer (grid of sibling products) ---------- */
  var initColourDrawer = function initPdpColourDrawer() {
    var drawer = document.querySelector('.js-pdpColourDrawer');
    var opener = document.querySelector('.js-pdpColourDrawerOpen');
    if (!drawer || !opener || !window.AT || !window.AT.drawer) return;

    var title = drawer.querySelector('.js-pdpColourDrawerTitle');
    window.AT.drawer.create(drawer, {
      openers: [opener],
      backdrop: document.querySelector('.js-pdpColourDrawerBackdrop'),
      label: (title && title.textContent.trim()) || 'Colors',
    });
  };

  onReady(function pdpBoot() {
    var sizeDrawer = initSizeDrawer();
    initSizeSelection();
    initAddToCart(sizeDrawer);
    initNotify(sizeDrawer);
    initColourDrawer();
    initReadMore();
    initDetailsTabs();
    initGalleryScrollbar();
    initGalleryDots();
    initGalleryVideos();
    initColourPreview();
    initSizeGuide();
    initAccordions();
  });
})();
